import { debounce } from '../js/global-exports.js';

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.maxResults = this.getAttribute('data-max-results');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.addEventListener('toggle', this.onToggle.bind(this), {capture: true})
  }

  onToggle(e) {
    const detailsOpened = e.target.open;
    if(detailsOpened) {
      const allDetails = this.querySelectorAll('details');

      allDetails.forEach((elem)=>{
        if(e.target !== elem) {
          elem.open = false;
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  static collectionResultTemplate(item) {
    return `
          <li class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${item.url}" class="predictive-search__item link link--text" tabindex="-1">
              ${item.title}
            </a>
          </li>
          `;
  }

  sortCollectionResults(searchTerm, searchResults) {
    // sort after importance - if the result starts with the search string or has a word that starts with it, it's assumed more relevant
    const searchResultsSorted = searchResults.map(entry => {
      let points = 0;

      if(searchTerm === entry.searchTitle.substr(0, searchTerm.length)){
        points += 2;
      }

      if(points === 0) {
        if(entry.searchTitle.indexOf(` ${searchTerm}`) !== -1) {
          points += 1;
        }
      }

      return {...entry, points};
    }).sort((a, b) => b.points - a.points);
    return searchResultsSorted;
  }

  getLocaleCollectionResults(searchTerm, resultsMarkup){
    if(window.collectionsForPredictiveSearch?.collectionInfo && window.collectionsForPredictiveSearch.collectionInfo.length){
      const formattedSearchTerm = searchTerm.toLowerCase().replaceAll('\'','&apos;');
      let searchResults = window.collectionsForPredictiveSearch.collectionInfo.filter((x)=>x.searchTitle.indexOf(formattedSearchTerm) !== -1);
      searchResults = this.sortCollectionResults(searchTerm, searchResults);
      // let newResultsMarkupOriginal = document.createRange().createContextualFragment(resultsMarkup);
      let newResultsMarkup = document.createRange().createContextualFragment(resultsMarkup);

      // remove collection result HTML from resultsMarkup (because it's in the wrong language)
      const primaryLanguageCollectionResults = newResultsMarkup.querySelector('.js-accordion--predictive-search-collection');
      if(primaryLanguageCollectionResults) {
        primaryLanguageCollectionResults.remove();
      }

      // if there are collection results
      if(searchResults.length) {
        const resultLimit = window.collectionsForPredictiveSearch.resultLimit;
        // "open" attribute should be removed from html in existing result since the collection results should be the open one
        const openAccordionItem = newResultsMarkup.querySelector('.accordion--predictive-search > details[open]');
        if(openAccordionItem) {
          openAccordionItem.removeAttribute('open');
        }

        // honor limit setting for collections
        if(searchResults.length > resultLimit) {
          searchResults = searchResults.slice(0, resultLimit)
        }

        // generate HTML for results
        const resultsHTML = searchResults
        .map(x=>this.constructor.collectionResultTemplate(x))
        .join('');

        // insert HTML for results into accordion
        const accordionHtmlString = `
        <div class="accordion accordion--predictive-search">
          <details open>
            <summary>
              <div class="summary__title">
                <h2 class="h4 accordion__title">
                  ${window.collectionsForPredictiveSearch.searchTranslation}
                </h2>
              </div>
              <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>
              </svg>
            </summary>
            <div class="accordion__content rte">
              <ul class="predictive-search__results-list predictive-search__results-list--collections list-unstyled" role="listbox">
                ${resultsHTML}
              </ul>
            </div>
          </details>
        </div>`;
        const accordionHtml = document.createRange().createContextualFragment(accordionHtmlString);

        // insert accordion into resultsMarkup as first result
        const elemToPrependResultsTo = newResultsMarkup.getElementById('predictive-search-results-list');
        elemToPrependResultsTo.prepend(accordionHtml);

      }

      // return results as html string
      const serializer = new XMLSerializer();
      newResultsMarkup = serializer.serializeToString(newResultsMarkup);
      return newResultsMarkup;
    }
    return resultsMarkup;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,page,article,collection,query&${encodeURIComponent('resources[limit_scope]')}=each&${encodeURIComponent('resources[limit]')}=${parseInt(this.maxResults, 10)}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        let resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        resultsMarkup = this.getLocaleCollectionResults(searchTerm, resultsMarkup);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    if(this.querySelector('.predictive-search--header')){
      this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
      return `${this.resultsMaxHeight}px`;
    }
    return 'none';
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
